import { getInhouseAnonymousId, getInhouseAnonymousIdFromCookie } from "src/utils/anonymousId";
import { getAnalytics } from "src/pages/helpers";
import { getPostHog } from "./posthog";
import { initGoogleAnalytics, captureGAPageView, trackGAEvent } from "./googleAnalytics";
import { initGoogleAdsense } from "./googleAdsense";
import { readCometToken } from "src/utils/cometToken";
import { EVENT_PARAMETERS } from "src/utils/EventConstants";

let isInitialized = false;
let inhouseAnonymousId = null;
const eventQueue = [];

/**
 * Get the stored inhouse_anonymous_id (synchronous)
 * Returns null if analytics hasn't been initialized yet
 */
export const getStoredInhouseAnonymousId = () => inhouseAnonymousId;

/**
 * Reset the in-memory anonymous ID after regeneration.
 * Call this after regenerateInhouseAnonymousId() so analytics uses the new ID.
 */
export const resetAnalyticsAnonymousId = (newId) => {
  inhouseAnonymousId = newId;
};

/**
 * Check if analytics has been initialized with the inhouse_anonymous_id
 */
export const isAnalyticsReady = () => isInitialized;

/**
 * Queue an event to be fired once analytics is ready
 * Returns true if the event was queued, false if analytics is already ready
 */
export const queueEvent = (type, eventName, data) => {
  if (isInitialized) return false;
  eventQueue.push({ type, eventName, data, timestamp: new Date().toISOString() });
  return true;
};

/**
 * Initialize analytics by:
 * 1. Getting inhouse_anonymous_id (waits for iframe, falls back to local UUID)
 * 2. Firing deferred Segment pageview with the ID
 * 3. Firing deferred PostHog pageview with the ID
 * 4. Flushing any queued events
 * 5. Marking analytics as ready
 */
export const initializeAnalytics = async () => {
  if (isInitialized) return inhouseAnonymousId;

  // 0. Initialize Google Analytics & Adsense
  initGoogleAnalytics();
  initGoogleAdsense();

  // 1. Check cookie SYNCHRONOUSLY first - immediate for returning users
  const existingId = getInhouseAnonymousIdFromCookie();

  if (existingId) {
    inhouseAnonymousId = existingId; // Set immediately for returning users!
    isInitialized = true; // Mark as ready immediately for returning users
  } else {
    // Only do async iframe logic for first-time visitors (no cookie)
    inhouseAnonymousId = await getInhouseAnonymousId();
  }

  // 2. Fire deferred Segment page view
  const analytics = await getAnalytics();

  // 2a. Identify user with inhouse_anonymous_id so root-level userId is populated
  if (analytics && inhouseAnonymousId) {
    analytics.identify(inhouseAnonymousId, {
      inhouse_anonymous_id: inhouseAnonymousId,
    });
  }

  // Capture pending page path before it gets deleted
  const pendingPagePath = window.__pendingPageView?.path || window.location.pathname;

  if (analytics && window.__pendingPageView) {
    analytics.page(window.__pendingPageView.path, {
      ...window.__pendingPageView,
      inhouse_anonymous_id: inhouseAnonymousId,
    });
    delete window.__pendingPageView;
  }

  // 3. Fire deferred PostHog page view
  const posthog = await getPostHog();
  if (posthog) {
    posthog.capture("$pageview", {
      $current_url: window.location.href,
      inhouse_anonymous_id: inhouseAnonymousId,
    });
  }

  // 3a. Fire deferred Google Analytics page view
  captureGAPageView(pendingPagePath);

  // 4. Flush queued events
  for (const event of eventQueue) {
    if (event.type === "track") {
      const eventData = { ...event.data, inhouse_anonymous_id: inhouseAnonymousId };
      // Override user_id if it was null/undefined when queued
      if (!eventData.user_id) {
        eventData.user_id = inhouseAnonymousId;
      }
      // The pixel may have loaded since this event was queued.
      if (!eventData[EVENT_PARAMETERS.COMET_TOKEN]) {
        const cometToken = readCometToken();
        if (cometToken) eventData[EVENT_PARAMETERS.COMET_TOKEN] = cometToken;
      }
      if (analytics) {
        analytics.track(event.eventName, eventData);
      }
      trackGAEvent(event.eventName, eventData);
    }
  }
  eventQueue.length = 0;

  // 5. Mark as ready (for first-time visitors; returning users are already marked ready above)
  isInitialized = true;
  return inhouseAnonymousId;
};
